/**
 * SoleadLab 2.0 — useShareViewer (F-02E-2A)
 *
 * Central read-only / share-viewer state. The contract is intentionally
 * narrow:
 *
 *   - `useIsShareViewer()` is true ONLY when a share session is `active`
 *     AND there is no authenticated Bearer token. This mirrors the
 *     `useShareMode = shareActive && !token` gate inside `apiFetch`
 *     ([services/apiFetch.ts:241-260]) so the UI and the network are
 *     guaranteed to agree about which mode we are in. An authenticated
 *     user landing on `/share/:token/viewer` therefore never enters
 *     visitor mode — the surrounding ShareViewerPage shows a "you are
 *     already signed in" notice instead.
 *
 *   - `useShareSectionAllowed(section)`:
 *       · In non-share mode → always true (the editor is fully open).
 *       · In share mode:
 *           - 'core' is always exposed (matches backend's implicit
 *             `core` section in `shareReadable` routes).
 *           - Optional sections (`tasks`, `iv`, `scores`, `state`,
 *             `monitoring`) are allowed iff the section appears in
 *             `shareSessionStore.view.exposed_sections`.
 *
 * Non-hook helpers (`isShareViewerState` / `isShareSectionAllowedState`)
 * mirror the same logic over plain state objects so that store-level
 * tests and any future imperative caller can run the same predicate
 * without React.
 *
 * Deliberately NOT added: a duplicate `uiStore.readOnly` flag. Source
 * of truth is `shareSessionStore.active` + `authStore.token`, and only
 * these stores drive the predicate.
 */

import { useShareSessionStore, type ShareView } from '../stores/shareSessionStore';
import { useAuthStore } from '../stores/authStore';

/**
 * Section identifiers recognized by the share-readable backend surface.
 * Mirrors `ALLOWED_EXPOSED_SECTIONS` in
 * soleadlab-api/src/schemas/shareLinks.ts plus the implicit 'core'
 * section which is always exposed.
 */
export type ShareViewerSection =
  | 'core'
  | 'tasks'
  | 'iv'
  | 'scores'
  | 'state'
  | 'monitoring'
  | 'layout';

/**
 * Pure predicate version of `useIsShareViewer` — same logic, accepts
 * snapshots of the relevant store state. Exported so non-React callers
 * and unit tests can exercise the rule without rendering hooks.
 */
export function isShareViewerState(
  shareActive: boolean,
  authToken: string | null,
): boolean {
  return shareActive && !authToken;
}

/**
 * Pure predicate version of `useShareSectionAllowed`.
 *
 *   - If `isShareViewer` is false, every section is allowed — the
 *     authenticated editor has no per-section gate.
 *   - In share mode, 'core' is always allowed; other sections require
 *     opt-in via `view.exposed_sections`.
 */
export function isShareSectionAllowedState(
  isShareViewer: boolean,
  view: ShareView | null,
  section: ShareViewerSection,
): boolean {
  if (!isShareViewer) return true;
  if (section === 'core') return true;
  if (!view) return false;
  return view.exposed_sections.includes(section);
}

/**
 * React hook: returns true iff the current tab is in share-viewer mode
 * (active share session AND no Bearer token). Re-renders on changes to
 * either store.
 */
export function useIsShareViewer(): boolean {
  const shareActive = useShareSessionStore((s) => s.active);
  const authToken = useAuthStore((s) => s.token);
  return isShareViewerState(shareActive, authToken);
}

/**
 * React hook: section-aware allow check, suitable for hiding rail
 * items / panels / individual cards.
 *
 * Performance note: the hook subscribes to three slices but each is a
 * primitive or an array reference held stable across renders by
 * Zustand, so the resulting re-render cost is bounded.
 */
export function useShareSectionAllowed(section: ShareViewerSection): boolean {
  const isShareViewer = useIsShareViewer();
  const view = useShareSessionStore((s) => s.view);
  return isShareSectionAllowedState(isShareViewer, view, section);
}

/**
 * F-02E-2B alias for `useIsShareViewer`. Read-only mode in the editor
 * shell currently means "we are in the share viewer" 1-for-1, but
 * `useIsReadOnlyViewer` reads more clearly at every call site that hides
 * a mutation control. Keeping a thin alias (instead of new state) avoids
 * any chance of drift between the network-level gate (`apiFetch`'s
 * `useShareMode`) and the UI-level gate; both ultimately resolve to
 * `shareSessionStore.active && !authStore.token`.
 *
 * If a future requirement adds a non-share read-only flavour (e.g.
 * "preview as visitor" for an owner, or a deploy/maintenance freeze),
 * widen the predicate inside this hook — every call site already routes
 * through it.
 */
export function useIsReadOnlyViewer(): boolean {
  return useIsShareViewer();
}

// ─── FL-1: field-link capabilities ──────────────────────────────────────────
//
// A kind='field' share link is STILL a read-only viewer globally
// (`useIsReadOnlyViewer` deliberately stays true for it — the 2026-08-04
// hardening is not reopened). The narrow write surface is exposed ONLY
// through this capability hook, consulted by the indication popup (and, in
// FL-2, quick-add). Server-side enforcement is authoritative; these flags
// just decide which affordances render.

export interface FieldCapabilities {
  /** True iff the visitor may edit the Notes field (properties.description). */
  canEditNotes: boolean;
  /** True iff the visitor may transition indication status. */
  canSetStatus: boolean;
  /** FL-2: true iff the visitor may quick-add new indications. */
  canCreate: boolean;
  /** FL-3a: true iff the visitor may attach field photos to indications. */
  canAddPhoto: boolean;
  /** The share-link id — localStorage scope key for the technician name. */
  linkId: string | null;
}

/** Pure predicate: is the active session a field-kind share visit? */
export function isFieldLinkState(
  shareActive: boolean,
  authToken: string | null,
  view: ShareView | null,
): boolean {
  return isShareViewerState(shareActive, authToken) && view?.kind === 'field';
}

export function useFieldCapabilities(): FieldCapabilities {
  const shareActive = useShareSessionStore((s) => s.active);
  const authToken = useAuthStore((s) => s.token);
  const view = useShareSessionStore((s) => s.view);
  const isField = isFieldLinkState(shareActive, authToken, view);
  return {
    canEditNotes: isField,
    canSetStatus: isField,
    canCreate: isField,
    canAddPhoto: isField,
    linkId: isField && view ? view.id : null,
  };
}

// ─── F-02E-2C: Rail-visibility helper ────────────────────────────────────────
//
// Pure predicate used by `EditorPage` to filter LEFT_RAIL / RIGHT_RAIL items
// in share-viewer mode. Owners always see every rail item their role allows
// (the role gating happens elsewhere in EditorPage). Share visitors see:
//
//   - Core display rails always: map, orthophoto, plans, vector-layout,
//     indications, calculator.
//   - Optional rails iff the section is exposed by the share link:
//     tasks → 'tasks', site-state → 'state', data-points / alarms →
//     'monitoring'.
//     NOTE (F-02E-5b): 'iv' does NOT unlock the iv-reporting ticket for
//     visitors. It unlocks the IV Results MAP LAYER — the legend entry in
//     LayersPanel plus the eager measurement load in ivStore. The ticket
//     (sessions, imports, string assignment) is an owner workflow surface
//     and stays hidden from share viewers.
//   - Owner-only / write-only rails never: project, media, copilot,
//     review-tools (findings/evidence/truth UI), and the monitoring
//     write tickets (data-source, sld, server-sync). These are already
//     hidden by F-02E-2B's mutation-control work; the rail filter here
//     enforces the same invariant at the rail-button level.
//
// Exported as a pure function so EditorPage can call it inline and unit
// tests can exercise every rail id without rendering React.

/** Rail item IDs (LEFT_RAIL + RIGHT_RAIL + bottom-rail owner controls). */
export type RailItemId =
  // Left rail
  | 'project' | 'map' | 'orthophoto' | 'plans' | 'vector-layout'
  | 'media' | 'indications' | 'calculator' | 'tasks' | 'iv-reporting'
  | 'site-state' | 'review-tools' | 'copilot'
  // Right rail
  | 'data-source' | 'data-points' | 'alarms' | 'sld' | 'server-sync'
  // Right rail — fault detection (admin/operator write surface; the
  // detection endpoints are never shareReadable, so the rail item is
  // hidden from share visitors outright)
  | 'detection'
  // Right rail — dock-demo (F-03E-5: hidden from share visitors)
  | 'dock-mission' | 'dock-settings'
  // Bottom-rail owner controls
  | 'org-settings' | 'team' | 'share-links' | 'storage-admin' | 'admin';

/**
 * Pure predicate: returns true iff a rail item should be visible in the
 * current session. Accepts plain state snapshots so unit tests can drive
 * every combination without a React harness.
 *
 *   - `isShareViewer === false` → always visible. Owner role/org gating
 *     happens at the call site (e.g. EditorPage's `canManageShareLinks`).
 *   - `isShareViewer === true` → see the per-id mapping above.
 */
export function isRailItemVisibleForShareViewer(
  itemId: RailItemId,
  isShareViewer: boolean,
  view: ShareView | null,
): boolean {
  if (!isShareViewer) return true;

  switch (itemId) {
    // Core display rails — always visible to share visitors.
    // The map renders overlays, orthophotos, layouts, and indications
    // directly, and the always-on LayersPanel exposes their
    // visibility/opacity controls. So 'map', 'indications', 'calculator'
    // are the only rails that add value beyond the map+legend itself.
    case 'map':
    case 'indications':
    case 'calculator':
      return true;

    // Optional sections — gated on exposed_sections.
    case 'tasks':
      return isShareSectionAllowedState(true, view, 'tasks');
    case 'site-state':
      return isShareSectionAllowedState(true, view, 'state');
    case 'data-points':
    case 'alarms':
      return isShareSectionAllowedState(true, view, 'monitoring');

    // Owner-only / write-only / out-of-scope — always hidden in share mode.
    // F-02E-2C: orthophoto, plans, vector-layout are editor management
    // tickets (upload PNG/PDF, register, crop, erase, anchor); their
    // DISPLAY is already handled by the map + LayersPanel and the
    // tickets add nothing for visitors. Hide to keep the rail honest.
    //
    // F-03E-5: dock-mission, dock-settings are the Dock Mission Planner +
    // Docking Station surfaces. Backend `/dock-missions` is not
    // shareReadable (403's visitors) but the panels can still render a
    // full local-only editor that misleads visitors into building phantom
    // missions; product decision is to hide the surface entirely from
    // share viewers until/unless a read-only dock-sharing flavour is
    // designed. Authenticated owners/operators are unaffected.
    //
    // F-02E-5b: iv-reporting is an owner workflow ticket (create sessions,
    // import files, assign strings). The 'iv' section exposes the IV
    // Results MAP LAYER to visitors instead — see LayersPanel + ivStore.
    case 'iv-reporting':
    case 'project':
    case 'media':
    case 'copilot':
    case 'review-tools':
    case 'orthophoto':
    case 'plans':
    case 'vector-layout':
    case 'data-source':
    case 'sld':
    case 'server-sync':
    case 'detection':
    case 'dock-mission':
    case 'dock-settings':
    case 'org-settings':
    case 'team':
    case 'share-links':
    case 'storage-admin':
    case 'admin':
      return false;
  }
}
